import {api} from '@/helpers/api';
import {ActionContext} from 'vuex';
import {IUserProfile} from '@/interfaces/user';
import {State} from '../state';
import {AdminState} from './state';
import {getStoreAccessors} from 'typesafe-vuex';
import {
  commitSetAddress,
  commitSetAdminSearchEngine,
  commitSetAdminSearchEngines,
  commitSetDefaultRoleSettings,
  commitSetExternalData,
  commitSetExternalDataList, commitSetExternalUser,
  commitSetExternalUsers,
  commitSetFormAdmin, commitSetFormCategories, commitSetFormCategory,
  commitSetFormsAdmin, commitSetGroup, commitSetGroups, commitSetInstitution, commitSetInstitutions,
  commitSetRole,
  commitSetRoles,
  commitSetUser,
  commitSetUsers,
} from './mutations';
import {dispatchCheckApiError} from '../main/actions';
import {commitAddNotification, commitRemoveNotification, commitSetPermissions} from '../main/mutations';
import {IForm, IFormCategory, IFormValues} from '@/interfaces/form';
import {IRole} from '@/interfaces/role';
import {IExternalData} from '@/interfaces/external-data';
import {ISearchEngine} from '@/interfaces/search_engine';
import {IDynamicQuery} from '@/interfaces';
import {IGroup} from "@/interfaces/group";
import {IInstitution} from "@/interfaces/institution";
import {eventBus} from "@/main";

type MainContext = ActionContext<AdminState, State>;

export const actions = {
  async actionGetUserAdmin(context: MainContext, payload: number) {
    try {
      const response = await api.getUser(payload);
      if (response) {
        commitSetUser(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetUsersAdmin(context: MainContext, payload?: IDynamicQuery) {
    try {
      const response = await api.getUsers(payload);
      if (response) {
        commitSetUsers(context, response.data.items);
      }
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  async actionUpdateUser(context: MainContext, payload: { id: number, user: IUserProfile }) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.updateUser(context.rootState.main.token, payload.id, payload.user);
      // const response = (await Promise.all([
      //    await api.updateUser(context.rootState.main.token, payload.id, payload.user),
      //     // await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
      // ]))[0];
      commitSetUser(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionCreateUser(context: MainContext, payload: { userProfile: IUserProfile, externalUserID: number }) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.createUser(context.rootState.main.token, payload.userProfile,
          payload?.externalUserID);
      commitSetUser(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteUser(context: MainContext, userId: number) {
    try {
      const loadingNotification = {content: 'deleting', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.deleteUser(userId);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionRegisterFormValuesInBatchAdmin(context: MainContext, payload: IFormValues[]) {
    try {
      const response = await api.registerFormValuesInBatch(context.rootState.main.token, payload);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return response;
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetFormsAdmin(context: MainContext) {
    try {
      const response = await api.getFormsAdmin();
      if (response) {
        commitSetFormsAdmin(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetForm(context: MainContext, form_id: number) {
    try {
      const response = await api.getForm(form_id);
      if (response) {
        commitSetFormAdmin(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionRegisterFormAdmin(context: MainContext, payload: IForm) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.registerForm(context.rootState.main.token, payload);
      commitSetFormAdmin(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateFormAdmin(context: MainContext, payload: IForm) {
    try {
      const loadingNotification = {content: 'updating', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.updateForm(context.rootState.main.token, payload);
      commitSetFormAdmin(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionDeleteFormAdmin(context: MainContext, payload: number) {
    try {
      const loadingNotification = {content: 'deleting', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.deleteForm(payload);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },

  async actionGetDefaultRoleSetting(context: MainContext) {
    try {
      const response = await api.getDefaultRoleSettings();
      if (response) {
        commitSetDefaultRoleSettings(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetRolesAdmin(context: MainContext) {
    try {
      const response = await api.getRoles();
      if (response) {
        commitSetRoles(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetRole(context: MainContext, id: number) {
    try {
      const response = await api.getRole(id);
      if (response) {
        commitSetRole(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionCreateRole(context: MainContext, payload: IRole) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.createRole(context.rootState.main.token, payload);
      commitSetRole(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateRole(context: MainContext, payload: IRole) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.updateRole(context.rootState.main.token, payload);
      commitSetRole(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteRole(context: MainContext, roleId: number) {
    try {
      const loadingNotification = {content: 'deleting', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.deleteRole(roleId);
      // commitSetRole(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },

  async actionGetExternalDataAdmin(context: MainContext, id: number) {
    try {
      const response = await api.getExternalData(id);
      if (response) {
        commitSetExternalData(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetExternalDataListAdmin(context: MainContext) {
    try {
      const response = await api.getExternalDataList();
      eventBus.$emit('SHOW_LOADING', true);
      if (response) {
        commitSetExternalDataList(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    } finally {
      eventBus.$emit('SHOW_LOADING', false);
    }
  },
  async actionCreateExternalData(context: MainContext, payload: IExternalData) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.createExternalData(context.rootState.main.token, payload);
      commitSetExternalData(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionExternalDataFileUpload(context: MainContext, payload: any) {
    try {
      const loadingNotification = {content: 'uploading', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.externalDataFileUpload(context.rootState.main.token, payload);
      if (response) {
        commitSetExternalData(context, response.data);
      }
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateExternalData(context: MainContext, payload: IExternalData) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.updateExternalData(context.rootState.main.token, payload);
      commitSetExternalData(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteExternalData(context: MainContext, id: number) {
    try {
      const loadingNotification = {content: 'deleting', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.deleteExternalData(id);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },

  async actionGetSearchEngineAdmin(context: MainContext, id: number) {
    try {
      const response = await api.getSearchEngine(id);
      if (response) {
        commitSetAdminSearchEngine(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetSearchEnginesAdmin(context: MainContext) {
    try {
      const response = await api.getSearchEngines();
      if (response) {
        commitSetAdminSearchEngines(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionCreateSearchEngine(context: MainContext, payload: ISearchEngine) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.createSearchEngine(context.rootState.main.token, payload);
      commitSetAdminSearchEngine(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateSearchEngine(context: MainContext, payload: ISearchEngine) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.updateSearchEngine(context.rootState.main.token, payload);
      commitSetAdminSearchEngine(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteSearchEngine(context: MainContext, id: number) {
    try {
      const loadingNotification = {content: 'deleting', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.deleteSearchEngine(id);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionGetExternalUserAdminById(context: MainContext, userId: number) {
    try {
      const response = await api.getExternalUser(userId);
      if (response) {
        commitSetExternalUser(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetExternalUsersAdmin(context: MainContext) {
    try {
      const response = await api.getExternalUsers();
      if (response) {
        commitSetExternalUsers(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionDeleteExternalUser(context: MainContext, id: number) {
    try {
      const loadingNotification = {content: 'deleting', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.deleteExternalUser(id);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  // Groups
  async actionGetGroup(context: MainContext, groupId: number) {
    try {
      const response = await api.getGroup(groupId);
      if (response) {
        commitSetGroup(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetGroups(context: MainContext) {
    try {
      const response = await api.getGroups();
      if (response) {
        commitSetGroups(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionCreateGroup(context: MainContext, payload: IGroup) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.createGroup(context.rootState.main.token, payload);
      commitSetGroup(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateGroup(context: MainContext, payload: IGroup) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.updateGroup(context.rootState.main.token, payload);
      commitSetGroup(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionDeleteGroup(context: MainContext, id: number) {
    try {
      const loadingNotification = {content: 'deleting', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.deleteGroup(id);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  // Permissions
  async actionGetPermissions(context: MainContext) {
    try {
      const response = await api.getPermissions();
      if (response) {
        commitSetPermissions(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },

  // Form Categories
  async actionGetFormCategory(context: MainContext, id: number) {
    try {
      const response = await api.getFormCategory(id);
      if (response) {
        commitSetFormCategory(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetFormCategories(context: MainContext) {
    try {
      const response = await api.getFormCategories();
      if (response) {
        commitSetFormCategories(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionCreateFormCategory(context: MainContext, payload: IFormCategory) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.createFormCategory(context.rootState.main.token, payload);
      commitSetFormCategory(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateFormCategory(context: MainContext, payload: IFormCategory) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.updateFormCategory(context.rootState.main.token, payload);
      commitSetFormCategory(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionDeleteFormCategory(context: MainContext, id: number) {
    try {
      const loadingNotification = {content: 'deleting', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.deleteFormCategory(id);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  // Institutions
  async actionGetInstitution(context: MainContext, id: number) {
    try {
      const response = await api.getInstitution(id);
      if (response) {
        commitSetInstitution(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetInstitutions(context: MainContext) {
    try {
      const response = await api.getInstitutions();
      if (response) {
        commitSetInstitutions(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionCreateInstitution(context: MainContext, payload: IInstitution) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.createInstitution(context.rootState.main.token, payload);
      commitSetInstitution(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateInstitution(context: MainContext, payload: IInstitution) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.updateInstitution(context.rootState.main.token, payload);
      commitSetInstitution(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionDeleteInstitution(context: MainContext, id: number) {
    try {
      const loadingNotification = {content: 'deleting', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.deleteInstitution(id);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  // Public
  async actionGetAddress(context: MainContext, cep: string) {
    try {
      const response = await api.getAddressFromCEP(cep);
      commitSetAddress(context, response.data)
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },

};

const {dispatch} = getStoreAccessors<AdminState, State>('');

export const dispatchGetUserAdmin = dispatch(actions.actionGetUserAdmin);
export const dispatchGetUsersAdmin = dispatch(actions.actionGetUsersAdmin);
export const dispatchCreateUser = dispatch(actions.actionCreateUser);
export const dispatchUpdateUser = dispatch(actions.actionUpdateUser);
export const dispatchDeleteUser = dispatch(actions.actionDeleteUser);

export const dispatchRegisterFormValuesInBatchAdmin = dispatch(actions.actionRegisterFormValuesInBatchAdmin);
export const dispatchGetForms = dispatch(actions.actionGetForm);
export const dispatchGetFormsAdmin = dispatch(actions.actionGetFormsAdmin);
export const dispatchRegisterForm = dispatch(actions.actionRegisterFormAdmin);
export const dispatchUpdateForm = dispatch(actions.actionUpdateFormAdmin);
export const dispatchDeleteForm = dispatch(actions.actionDeleteFormAdmin);

export const dispatchGetRole = dispatch(actions.actionGetRole);
export const dispatchGetRolesAdmin = dispatch(actions.actionGetRolesAdmin);
export const dispatchGetDefaultRoleSetting = dispatch(actions.actionGetDefaultRoleSetting);
export const dispatchCreateRole = dispatch(actions.actionCreateRole);
export const dispatchUpdateRole = dispatch(actions.actionUpdateRole);
export const dispatchDeleteRole = dispatch(actions.actionDeleteRole);

// export const dispatchGetExternalData = dispatch(actions.actionGetExternalData);
export const dispatchGetExternalDataListAdmin = dispatch(actions.actionGetExternalDataListAdmin);
export const dispatchCreateExternalData = dispatch(actions.actionCreateExternalData);
export const dispatchExternalDataFileUpload = dispatch(actions.actionExternalDataFileUpload);
export const dispatchUpdateExternalData = dispatch(actions.actionUpdateExternalData);
export const dispatchDeleteExternalData = dispatch(actions.actionDeleteExternalData);

export const dispatchGetSearchEngineAdmin = dispatch(actions.actionGetSearchEngineAdmin);
export const dispatchGetSearchEnginesAdmin = dispatch(actions.actionGetSearchEnginesAdmin);
export const dispatchCreateSearchEngine = dispatch(actions.actionCreateSearchEngine);
export const dispatchUpdateSearchEngine = dispatch(actions.actionUpdateSearchEngine);
export const dispatchDeleteSearchEngine = dispatch(actions.actionDeleteSearchEngine);


export const dispatchGetExternalUserAdminById = dispatch(actions.actionGetExternalUserAdminById);
export const dispatchGetExternalUsersAdmin = dispatch(actions.actionGetExternalUsersAdmin);
export const dispatchDeleteExternalUsersAdmin = dispatch(actions.actionDeleteExternalUser);


export const dispatchGetGroup = dispatch(actions.actionGetGroup);
export const dispatchGetGroups = dispatch(actions.actionGetGroups);
export const dispatchCreateGroup = dispatch(actions.actionCreateGroup);
export const dispatchUpdateGroups = dispatch(actions.actionUpdateGroup);
export const dispatchDeleteGroup = dispatch(actions.actionDeleteGroup);


export const dispatchGetPermissions = dispatch(actions.actionGetPermissions);


export const dispatchGetFormCategory = dispatch(actions.actionGetFormCategory);
export const dispatchGetFormCategories = dispatch(actions.actionGetFormCategories);
export const dispatchCreateFormCategory = dispatch(actions.actionCreateFormCategory);
export const dispatchUpdateFormCategory = dispatch(actions.actionUpdateFormCategory);
export const dispatchDeleteFormCategory = dispatch(actions.actionDeleteFormCategory);


export const dispatchGetInstitution = dispatch(actions.actionGetInstitution);
export const dispatchGetInstitutions = dispatch(actions.actionGetInstitutions);
export const dispatchCreateInstitution = dispatch(actions.actionCreateInstitution);
export const dispatchUpdateInstitution = dispatch(actions.actionUpdateInstitution);
export const dispatchDeleteInstitution = dispatch(actions.actionDeleteInstitution);


export const dispatchGetAddress = dispatch(actions.actionGetAddress);
